import { ColDef, CellClassParams, CellStyle, ValueGetterParams } from 'ag-grid-community';
import { css } from '@emotion/react';
import { isEqual } from 'lodash-es';
import { useMemo } from 'react';
import { useTheme } from '@mui/material';
import { AgTableWithBaseStyle } from '../../../components/AgTable/AgTable2';
import { TransactionHistoryTableData } from '../../../types';

type Props = {
  columnDefs: ColDef<TransactionHistoryTableData>[];
  data: TransactionHistoryTableData[];
};

type EnrichedRow = TransactionHistoryTableData & {
  _isCurrentRow: boolean;
  _diffFields: string[];
};

const defaultColDef: ColDef = {
  width: 180,
  minWidth: 150,
  maxWidth: 400,
  resizable: true,
  sortable: false,
  filter: false,
};

export const UpdatesHistoryTable = ({ columnDefs, data }: Props) => {
  const { colors } = useTheme();

  const diffFields = useMemo(() => {
    const getCellValue = (col: ColDef<TransactionHistoryTableData>, row: TransactionHistoryTableData) => {
      if (typeof col.valueGetter === 'function') {
        return col.valueGetter({ data: row, colDef: col } as ValueGetterParams<TransactionHistoryTableData>);
      }
      if (col.field) {
        return row[col.field as keyof TransactionHistoryTableData];
      }
      return undefined;
    };

    const fields = new Set<string>();
    if (data.length < 2) {
      return fields;
    }
    for (const col of columnDefs) {
      const key = col.colId ?? col.field ?? '';
      if (!key) {
        continue;
      }
      const v0 = getCellValue(col, data[0]);
      const v1 = getCellValue(col, data[1]);
      if ((v0 != null || v1 != null) && !isEqual(v0, v1)) {
        fields.add(key);
      }
    }
    return fields;
  }, [data, columnDefs]);

  const rowData: EnrichedRow[] = useMemo(() => {
    if (data.length < 2) {
      return [];
    }
    return [
      { ...data[0], _isCurrentRow: true, _diffFields: [...diffFields] },
      { ...data[1], _isCurrentRow: false, _diffFields: [...diffFields] },
    ];
  }, [data, diffFields]);

  const enrichedColumnDefs = useMemo(
    () =>
      columnDefs.map((col) => {
        const key = col.colId ?? col.field ?? '';
        const isDiffCol = diffFields.has(key);
        return {
          ...col,
          headerClass: isDiffCol ? 'ag-header-highlighted' : undefined,
          cellStyle: (params: CellClassParams<EnrichedRow>): CellStyle => {
            const isDiff = (params.data?._diffFields ?? []).includes(key);
            if (!isDiff) {
              return { color: colors.neutral[50] };
            }
            if (params.data?._isCurrentRow) {
              return { background: colors.success[10], color: colors.success[60] };
            }
            return {
              background: colors.critical[10],
              color: colors.critical[50],
              textDecoration: 'line-through',
              textDecorationColor: 'inherit',
            };
          },
        };
      }),
    [columnDefs, diffFields, colors]
  );

  const highlightCss = css`
    .ag-header-highlighted {
      background: ${colors.primary[10]} !important;
      .ag-header-cell-text {
        color: ${colors.primary[60]};
      }
    }
  `;

  return (
    <div css={highlightCss} style={{ width: '100%', marginBottom: '2rem' }}>
      <AgTableWithBaseStyle
        rowData={rowData}
        columnDefs={enrichedColumnDefs}
        domLayout='autoHeight'
        defaultColDef={defaultColDef}
        suppressMovableColumns
      />
    </div>
  );
};
